import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { chip, ib } from "../../ui/record";
import { area, refuse, select } from "../clients/forms";

// The patient's Care team · caregivers card on the Visits tab (brief 1D-3b, mockup v45 careTeam):
// who knows the patient, primaries and oriented backups with when and how each was oriented; the
// patient's Orientation choice (Off, Flag, Required, or as the client type); add a backup (picks a
// caregiver and schedules their orientation visit as a draft); make someone primary or a backup;
// end someone's place on the team with a reason. Small actions are icon buttons with hover tips.

interface Team {
  patient: { id: string; first: string; name: string };
  canEdit: boolean;
  orientation: { value: string; from: string; choice: string; label: string };
  choices: { value: string; label: string }[];
  members: {
    id: string;
    personId: string;
    name: string;
    title: string | null;
    role: "primary" | "backup";
    roleLabel: string;
    oriented: boolean;
    words: string;
  }[];
  backups: number;
  minBackups: number;
  noBackup: boolean;
  caregivers: { id: string; label: string }[];
}

/** Draws the card into `host`; `changed` runs after a change that adds visits (the list reloads). */
export async function careTeamCard(host: HTMLElement, patientId: string, changed: () => void) {
  const res = await api<Team>("GET", `/api/patients/${patientId}/caregivers`);
  if (!res.ok) {
    host.replaceChildren();
    return;
  }
  draw(res.body);

  function draw(t: Team) {
    const first = t.patient.first;
    const put = async (method: string, path: string, body: unknown, done: string, sub?: string) => {
      const r = await api<Team & { words?: string }>(method, path, body);
      if (!r.ok) {
        toast("That wasn't saved", r.body.error);
        return false;
      }
      toast(done, r.body.words ?? sub);
      draw(r.body);
      return true;
    };
    const acts: Node[] = [];
    if (t.canEdit) {
      const choice = el(
        "select",
        { id: "ct-req", "aria-label": "Orientation for this patient" },
        ...t.choices.map((c) =>
          el("option", { value: c.value, selected: c.value === t.orientation.choice }, c.label),
        ),
      );
      choice.addEventListener(
        "change",
        () =>
          void put(
            "PUT",
            `/api/patients/${patientId}/orientation`,
            { orientation: choice.value },
            "Orientation saved",
            `${first}'s orientation is ${choice.selectedOptions[0]?.textContent ?? ""}`,
          ),
      );
      acts.push(
        el("label", { class: "small", for: "ct-req" }, "Orientation"),
        choice,
        ib("person-add", "Add a backup (schedules an orientation visit)", () =>
          addBackup(t, async (body) => {
            const ok = await put(
              "POST",
              `/api/patients/${patientId}/caregivers`,
              body,
              "Orientation visit added",
            );
            if (ok) changed();
            return ok;
          }),
        ),
      );
    } else acts.push(chip(`Orientation: ${t.orientation.label}`, "plain"));

    const rows = t.members.map((m) =>
      el(
        "div",
        { class: "prow", role: "listitem", "aria-label": m.name },
        el(
          "span",
          {},
          el("b", {}, m.name),
          " ",
          el("span", { class: "small muted" }, m.title ?? ""),
          el("br"),
          el("span", { class: "small muted" }, m.words),
        ),
        el(
          "span",
          { class: "vacts" },
          chip(m.roleLabel, m.role === "primary" ? "ok" : "info"),
          ...(t.canEdit
            ? [
                ib(
                  "arrow-switch",
                  m.role === "primary" ? `Make ${m.name} a backup` : `Make ${m.name} primary`,
                  () =>
                    void put(
                      "PUT",
                      `/api/patients/${patientId}/caregivers/${m.id}`,
                      { role: m.role === "primary" ? "backup" : "primary" },
                      m.role === "primary" ? "Now a backup" : "Now primary",
                      m.name,
                    ),
                ),
                ib("x-circle", `End ${m.name}'s place on the team with a reason`, () =>
                  endPlace(m.name, (why) =>
                    put(
                      "POST",
                      `/api/patients/${patientId}/caregivers/${m.id}/end`,
                      { why },
                      "Off the care team",
                      `${m.name} is off the team`,
                    ),
                  ),
                ),
              ]
            : []),
        ),
      ),
    );
    host.replaceChildren(
      el(
        "section",
        { class: "card", "aria-label": "Care team · caregivers" },
        el(
          "div",
          { class: "cardhead" },
          el(
            "div",
            {},
            el("h2", {}, icon("people"), " Care team · caregivers"),
            el("span", { class: "small muted" }, `Who knows ${first}.`),
          ),
          el("span", { class: "actions" }, ...acts),
        ),
        rows.length
          ? el("div", { role: "list", "aria-label": "Care team" }, ...rows)
          : el("p", { class: "muted" }, "No one is oriented yet."),
        t.noBackup ? chip("No oriented backup", "warn") : null,
      ),
    );
  }
}

/** Add a backup: pick a caregiver; their orientation visit is drafted for the office to publish. */
function addBackup(t: Team, send: (body: unknown) => Promise<boolean>) {
  const who = select(
    "Caregiver",
    [["", "Choose a caregiver"], ...t.caregivers.map((c) => [c.id, c.label] as const)],
    "",
  );
  const go = el("button", { type: "button", class: "btn pri" }, icon("person-add"), " Add backup");
  const m = openModal({
    title: `Add a backup for ${t.patient.first}`,
    sub: "Schedules an orientation visit as a draft: a shadow visit with the primary (or an RN).",
    body: [el("div", { class: "form", style: "max-width:none" }, who.el)],
    actions: [go],
  });
  go.addEventListener("click", async () => {
    if (!who.input.value) return refuse(m.note, "Pick the caregiver.");
    go.disabled = true;
    const ok = await send({ caregiverId: who.input.value });
    go.disabled = false;
    if (ok) m.close();
  });
}

/** Ending a place on the team: why (kept with it, never deleted). */
function endPlace(name: string, send: (why: string) => Promise<boolean>) {
  const why = area("Why are they leaving the care team?", "");
  const go = el("button", { type: "button", class: "btn pri" }, icon("x-circle"), " End");
  const m = openModal({
    title: `End ${name}'s place on the team`,
    sub: "They come off the care team with the reason given.",
    body: [el("div", { class: "form", style: "max-width:none" }, why.el)],
    actions: [go],
  });
  go.addEventListener("click", async () => {
    if (!why.input.value.trim()) return refuse(m.note, "Say why.");
    if (await send(why.input.value)) m.close();
  });
}
